/**
 * ProShip Direct v2 — a courier's mark, where the record has one.
 *
 * THE LOGO IS DATA, NOT AN ASSET. The courier document carries `logoUrl` — it is already in the
 * field list of the panel's own persisted courier query (commonRTKPersist.js) and had simply never
 * been rendered. Shipping ~60 brand marks as local files would mean sourcing, licensing and
 * re-cutting them by hand, and going stale the day ops adds a partner. So this reads whatever the
 * record holds and nothing is checked into the repo.
 *
 * NO SUBSTITUTE MARK. An earlier version drew a lettered tile when `logoUrl` was empty, on the
 * reasoning that a half-filled column looks broken. It reads worse: a grid of invented monograms
 * is a column of marks that mean nothing, and it disguises the real state of the data — which is
 * that ops has not filled `logoUrl` in. Showing the logo where there is one and nothing where
 * there is not keeps the gap visible, and the gap is the thing worth fixing.
 *
 * Rendering nothing rather than an empty box is deliberate too: the row's flex `gap` only applies
 * between children that exist, so a courier with no mark gets no indent instead of an indent into
 * blank space.
 *
 * `contain`, NOT `cover`. Carrier logos are every aspect ratio there is — wide wordmarks, square
 * badges, tall shields. Avatar's default `cover` crops to fill, which slices the ends off the wide
 * ones and turns a wordmark into an unreadable middle.
 */
import React from 'react';
import { Box, Avatar, Tooltip } from '@mui/material';
import { WHITE, BORDER } from '../../../styles/foundation/colors';

export default function CourierLogo({ name, src, size = 26 }) {
  /*
    A dead URL is the same as no URL. Avatar would render its (now absent) children on a load
    failure, leaving an empty bordered box — so the border and background only exist while the
    image is actually showing, and `onError` drops the element entirely.
  */
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [src]);

  if (!src || failed) return null;

  const label = String(name || '').trim();

  return (
    <Tooltip title={label}>
      <Box sx={{ flexShrink: 0, display: 'flex' }}>
        <Avatar
          variant='rounded'
          src={src}
          alt={label}
          imgProps={{ onError: () => setFailed(true) }}
          sx={{
            width: size,
            height: size,
            borderRadius: '5px',
            /*
              White with a hairline, because most carrier marks are drawn for white and several are
              white-on-transparent — the border is what stops those dissolving into the row.
            */
            bgcolor: WHITE,
            border: `1px solid ${BORDER}`,
            '& .MuiAvatar-img': { objectFit: 'contain', p: '2px' },
          }}
        />
      </Box>
    </Tooltip>
  );
}
